import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { useStorage } from '~Components/Storage';
import { useLogger } from '~Hooks/Entities/useLogger';
import * as actions from '~actions';

/**
 * Automatic staff logout after an order has been handled.
 *
 * When the `logoutStaffAfterOrder` setting is enabled, the currently logged-in staff
 * member is logged out after:
 *  - an order has been sent to the kitchen (bonged), and
 *  - a payment has been completed.
 *
 * The purpose is to make sure the next staff member has to log in with their own
 * code before a new order or payment is handled.
 *
 * Returns a function that performs the logout (if the setting is enabled). Call it at
 * the very end of the bong / payment flow, after all bookkeeping that depends on the
 * staff id (competitions, logging, etc.) has been done.
 */
export const useAutoStaffLogout = () => {
  const [storage] = useStorage();
  const dispatch = useDispatch();
  const logger = useLogger('auto-staff-logout');

  const enabled = storage.logoutStaffAfterOrder;

  /**
   * Logs the staff out if the setting is enabled.
   * @param reason Why the logout is triggered, used for logging only.
   */
  const logoutIfEnabled = useCallback(
    (reason: 'bong' | 'payment') => {
      if (!enabled) {
        return;
      }

      logger.info('Automatically logging out staff after order', { reason });
      dispatch(actions.staffLogout());
    },
    [enabled, dispatch],
  );

  return { enabled, logoutIfEnabled };
};
